import { PencilSimple } from "@phosphor-icons/react";
import { ddMmmYy, fmtDateTime } from "../lib/format";

// Shared "edited" audit-trail UI: a colored pill of WHAT changed on a PO line + WHO/WHEN.
// Used inside the PO detail modal (per-line) and on the PO / Open-Order tables (per-row summary).

export const FIELD_STYLE = {
  due:    { bg: "#EFF6FF", fg: "#1D4ED8", br: "#BFDBFE" },
  qty:    { bg: "#FEF3C7", fg: "#92400E", br: "#FDE68A" },
  price:  { bg: "#F5F3FF", fg: "#6D28D9", br: "#DDD6FE" },
  cancel: { bg: "#FEF2F2", fg: "#991B1B", br: "#FECACA" },
  custpo: { bg: "#F1F5F9", fg: "#334155", br: "#CBD5E1" },
  added:  { bg: "#ECFDF5", fg: "#047857", br: "#A7F3D0" },
};

export function relTime(iso) {
  const then = iso ? new Date(iso).getTime() : 0;
  if (!then) return "";
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return "just now";
  const m = s / 60; if (m < 60) return `${Math.floor(m)}m ago`;
  const h = m / 60; if (h < 24) return `${Math.floor(h)}h ago`;
  const d = h / 24; if (d < 30) return `${Math.floor(d)}d ago`;
  return ddMmmYy(iso);
}

function tipFor(edit) {
  const when = edit.at ? new Date(edit.at) : null;
  return `${edit.summary}\nby ${edit.by || "?"}${when && !isNaN(when) ? " · " + fmtDateTime(edit.at) : ""}`;
}

// Full per-line chip (used inside the detail modal): pill of the change + who/when beneath.
export function LineEditChip({ edit, idx }) {
  if (!edit || !edit.summary) return null;
  const st = FIELD_STYLE[edit.field] || FIELD_STYLE.custpo;
  return (
    <div data-testid={`po-line-lastedit-${idx}`} title={tipFor(edit)}
      style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontFamily: "system-ui, sans-serif" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999,
        fontSize: 10.5, fontWeight: 700, background: st.bg, color: st.fg, border: `1px solid ${st.br}` }}>
        <PencilSimple size={11} weight="bold" /> {edit.summary}
      </span>
      <span style={{ fontSize: 10.5, fontWeight: 500, color: "#94A3B8" }}>
        {edit.by || "?"} · {relTime(edit.at)}
      </span>
    </div>
  );
}

// Most-recent line edit across a PO's lines, plus how many lines were edited.
export function latestLineEdit(po) {
  const lines = (po && po.lines) || [];
  let best = null, count = 0;
  for (const l of lines) {
    const e = l && l.last_edit;
    if (e && e.at) {
      count += 1;
      if (!best || String(e.at) > String(best.at)) best = e;
    }
  }
  return { edit: best, count };
}

// Compact table pill: shows the latest change summary (or "Edited"), a "+N" when several lines
// were edited, and who/when on hover. `edit` may come from a single line or from latestLineEdit().
export function EditedPill({ edit, count = 1, showSummary = true, testid }) {
  if (!edit || !edit.summary) return null;
  const st = FIELD_STYLE[edit.field] || FIELD_STYLE.custpo;
  const extra = count > 1 ? ` +${count - 1}` : "";
  const tip = tipFor(edit) + (count > 1 ? `\n(${count} lines edited on this PO)` : "");
  return (
    <span data-testid={testid} title={tip}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "1px 7px", borderRadius: 999,
        fontSize: 10, fontWeight: 700, lineHeight: 1.5, background: st.bg, color: st.fg,
        border: `1px solid ${st.br}`, whiteSpace: "nowrap", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
      <PencilSimple size={10} weight="bold" /> {showSummary ? edit.summary : "Edited"}{extra}
    </span>
  );
}
